import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {parsePanelDate} from '../panel_v11/panelFormatters';
import {SortableTh} from '../../elements/SortableTh';
import {SORT_DESC, nextSort, sortRows} from '../../../utils/sortRows';
import {relativeTimeLabel} from '../../portal_usage/portalUsageState';

// El valor por el que se ordena cada columna. `null` = no tiene el dato (nunca entró) y va al final
// en las dos direcciones (`utils/sortRows.js`).
const SORT_VALUE = {
    person: (user) => String(user.name || user.email || '').toLocaleLowerCase(),
    queries: (user) => Number(user.assistant_queries) || 0,
    views: (user) => Number(user.portal_views) || 0,
    last: (user) => {
        const date = parsePanelDate(user.last_at);
        return date ? date.getTime() : null;
    },
};

/**
 * «Uso por persona»: cuánto usa el portal cada persona del cliente (card k14eI6RC).
 *
 * Leo (Bakián), en la call del 07/10: «quiero ver por usuario». El detalle era sólo por cliente,
 * pero `GET client/<id>/portal-usage/` ya traía `portal_users[]` con lo de cada uno y la pantalla
 * no lo dibujaba. Es la misma respuesta, sin pedir nada más.
 *
 * Tres decisiones:
 *  - **Consultas, no pasos.** «Pasos» salió de los resúmenes de esta pantalla en la misma card:
 *    nadie entendía qué eran. `assistant_steps` viaja y no se muestra.
 *  - **La última actividad sale de `last_at`, no de `last_login_at`**: el login sólo se mueve al
 *    loguearse, y quien deja la sesión abierta quedaría «sin entrar» desde hace semanas.
 *  - **Arranca ordenada por consultas, de mayor a menor**: es la pregunta con la que se abre. Los
 *    encabezados ordenan a pedido; el tercer clic deja el orden en que llegaron.
 *
 * Nombre y email los escribió alguien: van como children de React, nunca interpolados en una clave.
 */
export function PortalUsersUsage({users, windowDays}) {
    const {t} = useTranslation();
    const [sort, setSort] = useState({column: 'queries', direction: SORT_DESC});
    const list = Array.isArray(users) ? users.filter(Boolean) : [];
    if (!list.length) return null;

    const rows = sortRows(list, sort.column ? SORT_VALUE[sort.column] : null, sort.direction);
    const onSort = (column, firstDirection) => setSort((prev) => nextSort(prev, column, firstDirection));

    return (
        <div className="box pu-box">
            <div className="pu-box__head">
                <h3 className="pu-box__title">{t('portal_usage.users_title')}</h3>
            </div>
            <p className="pu-box__sub">
                {t('portal_usage.users_sub', {days: windowDays || 30})}
            </p>

            <table className="pu-table pu-users">
                <thead>
                    <tr>
                        <SortableTh label={t('portal_usage.users_col_person')} column="person"
                                    sort={sort} onSort={onSort} firstDirection="asc"/>
                        <SortableTh label={t('portal_usage.users_col_queries')} column="queries"
                                    sort={sort} onSort={onSort} className="-num"/>
                        <SortableTh label={t('portal_usage.users_col_views')} column="views"
                                    sort={sort} onSort={onSort} className="-num"/>
                        <SortableTh label={t('portal_usage.users_col_last')} column="last"
                                    sort={sort} onSort={onSort}/>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((user) => (
                        <tr key={user.id || user.email}>
                            <td className="pu-users__who">
                                <strong>{user.name || user.email}</strong>
                                {user.name && user.email ? <small>{user.email}</small> : null}
                            </td>
                            <td className="-num">{Number(user.assistant_queries) || 0}</td>
                            <td className="-num">{Number(user.portal_views) || 0}</td>
                            <td className="-muted">
                                {relativeTimeLabel(user.last_at, t) || t('portal_usage.users_never')}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export default PortalUsersUsage;
